import React from 'react';
import { Mail, Linkedin, Github, Send, MapPin, Phone } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { personalInfo } from '../data/mock';
import { FileText } from 'lucide-react';
import ScrollReveal from './ScrollReveal';
import MagneticButton from './MagneticButton';
import SectionHeader from './SectionHeader';

const Contact = () => {
  const contactMethods = [
    {
      icon: Mail,
      label: 'Email',
      value: personalInfo.email,
      href: `mailto:${personalInfo.email}`
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      value: 'Connect with me',
      href: personalInfo.socialLinks.linkedin
    },
    {
      icon: FileText,
      label: 'Medium',
      value: 'Read my articles',
      href: personalInfo.socialLinks.medium
    }
  ];

  return (
    <section id="contact" className="py-16 relative bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <ScrollReveal>
          <SectionHeader 
            title="Get In Touch" 
            subtitle="I'm always open to discussing new opportunities, collaborations, or just having a chat about cloud engineering"
          />
        </ScrollReveal>

        <div className="max-w-4xl mx-auto">
          {/* Contact Methods */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <ScrollReveal key={index} delay={index * 100} direction="up">
                  <a
                    href={method.href}
                    target={method.href.startsWith('http') ? '_blank' : undefined}
                    rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="block"
                  >
                    <Card className="p-6 text-center bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-lg transition-all duration-300 group h-full">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-md">
                        <IconComponent size={24} className="text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{method.label}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 break-words">{method.value}</p>
                    </Card>
                  </a>
                </ScrollReveal>
              );
            })}
          </div>

          {/* Contact Info */}
          <ScrollReveal delay={300}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300 justify-center">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <MapPin size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <span className="font-medium">{personalInfo.location}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300 justify-center">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Phone size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <span className="font-medium">{personalInfo.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300 justify-center">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Mail size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <span className="font-medium text-sm">{personalInfo.email}</span>
              </div>
            </div>
          </ScrollReveal>

          {/* CTA Card */}
          <ScrollReveal delay={400} direction="up">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-800 border-2 border-blue-200 dark:border-gray-700">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full mb-6 shadow-lg">
                  <Send size={28} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Let's Build Something Great Together
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                  Whether you're looking to optimize your cloud infrastructure, implement DevOps best practices, or need consultation on AWS solutions, I'd love to hear from you.
                </p>
                <MagneticButton strength={0.15}>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
                    onClick={() => window.location.href = `mailto:${personalInfo.email}`}
                  >
                    <Mail size={20} className="mr-2" />
                    Send me an email
                  </Button>
                </MagneticButton>
              </div>
            </Card>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;